import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLang, tr } from "../contexts/LangContext";
import api from "../lib/api";
import { Check, Clock, XCircle } from "lucide-react";

export default function CheckoutComplete() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("pending");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    let attempts = 0;
    let cancelled = false;
    const fetchStatus = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        if (cancelled) return;
        setOrder(data);
        setStatus(data.status);
        if (data.status === "pending" && attempts < 5) {
          setTimeout(fetchStatus, 1500);
        }
      } catch {
        /* ignore */
      }
    };
    fetchStatus();
    return () => { cancelled = true; };
  }, [orderId]);

  if (!orderId) {
    return <div className="pt-32 pb-24 text-center text-slate-500" data-testid="checkout-missing-order">Missing order.</div>;
  }

  const noPayment = status === "pending" && order && !order.mollie_payment_id;
  const ui = status === "paid"
    ? { Icon: Check, color: "text-green-600", bg: "bg-green-50", border: "border-green-200", title: t.shop.paymentSuccess, text: t.shop.paymentSuccessText }
    : status === "payment_failed"
    ? { Icon: XCircle, color: "text-red-600", bg: "bg-red-50", border: "border-red-200", title: t.shop.paymentFailed, text: t.shop.paymentFailed }
    : noPayment
    ? { Icon: Check, color: "text-club-yellow-dark", bg: "bg-yellow-50", border: "border-yellow-200", title: tr("Commande enregistrée"), text: t.shop.noOnlinePayment }
    : { Icon: Clock, color: "text-club-yellow-dark", bg: "bg-yellow-50", border: "border-yellow-200", title: t.shop.paymentPending, text: t.shop.paymentPending };

  return (
    <div className="pt-32 pb-24 min-h-screen bg-sand" data-testid="checkout-complete">
      <div className="max-w-2xl mx-auto px-6 md:px-10 text-center">
        <div className={`inline-flex w-20 h-20 ${ui.bg} ${ui.border} border rounded-full items-center justify-center mb-8`}>
          <ui.Icon className={`w-10 h-10 ${ui.color}`} strokeWidth={1.5} />
        </div>
        <h1 className="font-serif text-4xl text-navy" data-testid="checkout-status-title">{ui.title}</h1>
        <div className="divider-gold mx-auto mt-6 mb-6" />
        <p className="text-slate-600">{ui.text}</p>
        {order && (
          <div className="mt-8 bg-white border border-slate-200 p-6 text-left">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{tr("Commande")}</div>
            <div className="font-mono text-sm text-navy">{order.id}</div>
            <div className="mt-3 text-lg font-serif text-navy">Total : € {order.total.toFixed(2)}</div>
          </div>
        )}
        <Link to="/shop" className="inline-block mt-8 bg-navy text-white px-8 py-3 text-xs uppercase tracking-[0.25em] hover:bg-club-red transition-colors" data-testid="back-to-shop-complete">
          {t.shop.continueShopping}
        </Link>
      </div>
    </div>
  );
}
